import _ from "lodash";
import { SubstanceRow, YesNoValue } from "./types";

/**
 * Field inventory of the contamination declaration. The backend publishes the
 * question catalog as a TDA list (labels, sections, required markers, quick
 * picks); this file only fixes *which* typed fields exist on the declaration
 * and how the form treats them, so the diff-based save can be a pure function.
 */

// In the printed form's (4a) row order. The paper prints its first four hazards
// on one row; the backend asks them individually, so the fourth entry here is
// the one that has no row of its own on paper.
export const CONTAMINATION_FIELDS = [
    'contaminationBiological',
    'contaminationExplosive',
    'contaminationRadioactive',
    'contaminationMercuryCyanideDioxinsSodiumAzidePcbs',
    'contaminationArsenicCadmiumPhosphorous',
    'contaminationCopperProcess',
    'contaminationToxic',
    'contaminationCorrosive',
    'contaminationPyrophoric',
    'contaminationOther',
] as const;
export type ContaminationField = typeof CONTAMINATION_FIELDS[number];

// "Return for testing/part qualification only" is a statement about why the asset
// is coming back, not about its past, which is why Rev. 2 prints it inside the
// Reason-for-service panel. It keeps its own constant so the section grouping
// stays honest while it still reaches YES_NO_FIELDS below.
export const REASON_YES_NO_FIELDS = [
    'returnForTestingOnly',
] as const;

export const CONDITION_YES_NO_FIELDS = [
    'equipmentWasUsed',
    'decontaminated',
] as const;

export const YES_NO_FIELDS = [
    ...REASON_YES_NO_FIELDS,
    ...CONDITION_YES_NO_FIELDS,
    ...CONTAMINATION_FIELDS,
] as const;
export type YesNoField = typeof YES_NO_FIELDS[number];

export const TEXT_FIELDS = [
    'invoiceNumber',
    'purchaseOrderNumber',
    'reasonForService',
    'oilType',
    'substancesAdditionalInformation',
    'billingAddress',
    'shippingAddress',
    'technicianName',
    'technicianPhone',
    'technicianEmail',
] as const;
export type TextField = typeof TEXT_FIELDS[number];

/** Free-text answers the form renders as multi-line inputs. */
export const MULTILINE_TEXT_FIELDS: readonly TextField[] = [
    'reasonForService',
    'substancesAdditionalInformation',
    'billingAddress',
    'shippingAddress',
];

export const RUNNING_TIME_HOURS_FIELD = 'runningTimeHours';
export const SUBSTANCES_FIELD = 'substances';
export const DECLARATION_ACCEPTED_FIELD = 'declarationAccepted';
export const DECONTAMINATED_FIELD: YesNoField = 'decontaminated';
export const CONTAMINATION_OTHER_FIELD: ContaminationField = 'contaminationOther';
export const SUBSTANCES_ADDITIONAL_INFORMATION_FIELD: TextField = 'substancesAdditionalInformation';
export const REASON_FOR_SERVICE_FIELD: TextField = 'reasonForService';

export type ContaminationDeclarationFormValues =
    Record<TextField, string> &
    Record<YesNoField, YesNoValue> & {
        /** Kept as the typed string so separators can be stripped on save. */
        runningTimeHours: string;
        substances: SubstanceRow[];
        declarationAccepted: boolean;
    };

export const isYesNoField = (index: string): index is YesNoField =>
    (YES_NO_FIELDS as readonly string[]).includes(index);

export const isTextField = (index: string): index is TextField =>
    (TEXT_FIELDS as readonly string[]).includes(index);

export const isContaminationField = (index: string): index is ContaminationField =>
    (CONTAMINATION_FIELDS as readonly string[]).includes(index);

/** Section keys as published in the attributes' `categories`, in render order. */
export const SECTION_KEYS = [
    'descriptionOfEquipment',
    'reasonForService',
    'conditionOfEquipment',
    'contamination',
    'harmfulSubstances',
    'contactInformation',
    'legallyBindingDeclaration',
] as const;
export type SectionKey = typeof SECTION_KEYS[number];

export const HARMFUL_SUBSTANCES_SECTION: SectionKey = 'harmfulSubstances';
export const CONTAMINATION_SECTION: SectionKey = 'contamination';
export const CONTACT_INFORMATION_SECTION: SectionKey = 'contactInformation';

// The form content is English by design (it mirrors the official PDF), so the
// section titles are not translated; unknown keys fall back to a start-cased key.
const SECTION_TITLES: Record<SectionKey, string> = {
    descriptionOfEquipment: 'Description of asset or test samples',
    reasonForService: 'Reason for service',
    conditionOfEquipment: 'Condition of asset',
    contamination: 'Process related contamination & exposure of asset',
    harmfulSubstances: 'Substances, gases and by-products in contact with the asset',
    contactInformation: 'Contact information',
    legallyBindingDeclaration: 'Legally binding declaration',
};

export const getSectionTitle = (key: string): string =>
    (SECTION_TITLES as Record<string, string>)[key] ?? _.startCase(key);
